import {
  AHEmptyScreen,
  AHSeparator,
  AHText,
  Box,
  ListItem,
  TodoPrioIcon,
} from "arkh";
import React, {
  Suspense,
  useCallback,
  useDeferredValue,
  useMemo,
  useState,
} from "react";
import { FlatList } from "react-native-gesture-handler";
import { graphql, useLazyLoadQuery, usePaginationFragment } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../components/mobile/QueryNetworkBoundrary";
import { WorkspaceTodoStackScreenProps } from "../WorkspaceTodoStack";
import { WorkspaceTodosArchivedQuery } from "./__generated__/WorkspaceTodosArchivedQuery.graphql";
import { WorkspaceTodosArchivedList_archiveTodos$key } from "./__generated__/WorkspaceTodosArchivedList_archiveTodos.graphql";
import { useOrgHomeDetailLayout } from "../../useOrgHomeDetailContainerStyle";
import { ColumnTitle } from "../../SortableColumnTitle";

import { WorkspaceTodoListItemStatus } from "../WorkspaceTodoListItemStatus";
import { WorkspaceTodoListItemDescription } from "../WorkspaceTodoListItemDescription";
import {
  WorkspaceTodoPrioColumnContainer,
  WorkspaceTodoStatusColumnContainer,
} from "../WorkspaceTodoItemRowContainer";
import { OrgSearchInput } from "../../OrgSearchInput";
import { FormattedMessage, useIntl } from "react-intl";
import { useIntlTokens } from "../../../../components/mobile/useIntlTokens";
import { LoadingList } from "../../WorkspaceFiles/LoadingList";
import { WorkspaceEmptyListItem } from "../../WorkspaceEmptyListItem";
import { Image } from "expo-image";
import { useWorkspaceId } from "../../workspaceId";
import { FormattedDateInWorkspace } from "../../../../common/dates/FormattedDate";
import { WorkspaceDetailNavigationHeader } from "../../WorkspaceDetailNavigationHeader";
import { HeaderButton } from "../../../../components/mobile/Button/HeaderButtons";

export function WorkspaceTodosArchived(
  props: WorkspaceTodoStackScreenProps<"WorkspaceTodosArchived">
) {
  const intl = useIntl();
  const openTodo = useCallback(
    (todoWorkspaceId: string) => {
      props.navigation.navigateDeprecated("WorkspaceTodoDetail", {
        todoWorkspaceId,
      });
    },
    [props.navigation]
  );
  return (
    <>
      <WorkspaceDetailNavigationHeader
        left={
          <HeaderButton
            type="WEB-BACK"
            onPress={() => props.navigation.goBack()}
            customLabel={intl.formatMessage({
              defaultMessage: "All Todos",
              description: "WorkspaceTodosOccasion headerBackTitle",
            })}
          />
        }
        right={null}
      />
      <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
        {(fetchKey) => (
          <WorkspaceTodosArchivedComponent
            fetchKey={fetchKey}
            openTodo={openTodo}
          />
        )}
      </LazyQueryNetworkBoundrary>
    </>
  );
}

function WorkspaceArchivedTodosTitle() {
  return (
    <FormattedMessage
      defaultMessage="Archived Todos"
      description="WorkspaceTodosArchived header title"
    />
  );
}

function WorkspaceTodosArchivedComponent(props: {
  fetchKey: string;

  openTodo: (todoWorkspaceId: string) => void;
}) {
  const workspaceId = useWorkspaceId();

  const [search, setSearch] = useState("");

  const query = useDeferredValue(search);

  const data = useLazyLoadQuery<WorkspaceTodosArchivedQuery>(
    graphql`
      query WorkspaceTodosArchivedQuery(
        $workspaceId: ID!
        $after: String
        $first: Int
        $query: String
      ) {
        workspace(id: $workspaceId) {
          id
          ...WorkspaceTodosArchivedList_archiveTodos
        }
      }
    `,
    {
      workspaceId,
      query,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const header = useMemo(() => {
    return (
      <Box color="surface-background-light" spaceTop="medium">
        <OrgSearchInput value={search} onChangeText={setSearch} />
        <WorkspaceTodosArchivedListItemHeader />
      </Box>
    );
  }, [search]);

  return (
    <Suspense fallback={<LoadingList header={header} />}>
      <WorkspaceTodosArchivedList
        workspace={data.workspace}
        openTodo={props.openTodo}
        header={header}
      />
    </Suspense>
  );
}

function WorkspaceTodosArchivedList(props: {
  workspace: WorkspaceTodosArchivedList_archiveTodos$key;
  header: React.ReactElement;
  openTodo: (todoWorkspaceId: string) => void;
}) {
  const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment WorkspaceTodosArchivedList_archiveTodos on Workspace
      @refetchable(
        queryName: "WorkspaceTodosArchivedList_WorkspaceRefetchQuery"
      ) {
        id
        hasArchivedTodo
        archivedTodos(first: $first, after: $after, query: $query)
          @connection(
            key: "WorkspaceTodosArchivedList_archiveTodos_archivedTodos"
          ) {
          edges {
            cursor
            node {
              id

              title
              priority
              publishedAtDate {
                ...FormattedDate
              }
              archivedAtDate {
                ...FormattedDate
              }
              expireAtDate {
                ...FormattedDate
              }

              ...WorkspaceTodoListItemStatus_todo
              ...WorkspaceTodoListItemDescription_todo
            }
          }
        }
      }
    `,
    props.workspace
  );

  const listData = useMemo(() => {
    return data.archivedTodos.edges.filter((e) => e.node?.id);
  }, [data.archivedTodos.edges]);

  const detailLayout = useOrgHomeDetailLayout();

  const onReachEnd = useCallback(() => {
    if (hasNext && !isLoadingNext) {
      loadNext(30);
    }
  }, [hasNext, isLoadingNext, loadNext]);

  if (!data.hasArchivedTodo) {
    return (
      <AHEmptyScreen
        title={<WorkspaceArchivedTodosTitle />}
        // TODO: Intl
        subtitle="Det finns inga arkiverade aktiviteter. När du arkiverar en aktivitet hamnar den här."
        illustration={
          <Box style={{ flex: 1, minWidth: 500 }}>
            <Image
              contentFit="contain"
              contentPosition="top center"
              style={{ flex: 1 }}
              source={require("./workspace_todo_archive_empty.png")}
            />
            <Image
              contentFit="contain"
              style={{ flex: 1 }}
              source={require("./workspace_todo_archive_empty.png")}
            />
            <Image
              contentFit="contain"
              style={{ flex: 1 }}
              source={require("./workspace_todo_archive_empty.png")}
            />
          </Box>
        }
      />
    );
  }

  return (
    <FlatList
      ListHeaderComponent={props.header}
      onEndReached={onReachEnd}
      contentContainerStyle={detailLayout.contentContainerStyle}
      data={listData}
      renderItem={({ item, index }) => (
        <ListItem
          isLast={index + 1 === listData.length}
          onPress={() => {
            props.openTodo(item.node.id);
          }}
          border="border-primary"
        >
          <WorkspaceTodosArchivedListItemContainer
            status={<WorkspaceTodoListItemStatus workspaceTodo={item.node} />}
            archivedAt={
              <AHText numberOfLines={1}>
                {item.node.archivedAtDate ? (
                  <FormattedDateInWorkspace date={item.node.archivedAtDate} />
                ) : null}
              </AHText>
            }
            priority={<TodoPrioIcon priority={item.node.priority} />}
            pulishedAt={
              <AHText numberOfLines={1}>
                {item.node.publishedAtDate ? (
                  <FormattedDateInWorkspace date={item.node.publishedAtDate} />
                ) : null}
              </AHText>
            }
            description={
              <WorkspaceTodoListItemDescription workspaceTodo={item.node} />
            }
          />
        </ListItem>
      )}
      ItemSeparatorComponent={AHSeparator}
      stickyHeaderIndices={[0]}
      ListEmptyComponent={WorkspaceEmptyListItem}
      keyExtractor={(item) => item.node.id}
    />
  );
}

function WorkspaceTodosArchivedListItemContainer(props: {
  priority: React.ReactElement;
  description: React.ReactElement;
  pulishedAt: React.ReactElement;
  archivedAt: React.ReactElement;
  status: React.ReactElement;
}) {
  return (
    <Box style={{ flexDirection: "row", alignItems: "center" }}>
      <WorkspaceTodoPrioColumnContainer>
        {props.priority}
      </WorkspaceTodoPrioColumnContainer>
      <Box style={{ flex: 1, flexDirection: "row", alignItems: "center" }}>
        <Box style={{ width: "40%" }} spaceRight="medium">
          {props.description}
        </Box>
        <Box style={{ width: "20%" }} spaceRight="medium">
          {props.pulishedAt}
        </Box>
        <Box style={{ width: "20%" }}>{props.archivedAt}</Box>
      </Box>
      <WorkspaceTodoStatusColumnContainer>
        {props.status}
      </WorkspaceTodoStatusColumnContainer>
    </Box>
  );
}

function WorkspaceTodosArchivedListItemHeader() {
  const intl_tokens = useIntlTokens();
  return (
    <Box color="surface-background-light">
      <ListItem disabled border="border-primary">
        <WorkspaceTodosArchivedListItemContainer
          status={<ColumnTitle>{intl_tokens.Status}</ColumnTitle>}
          priority={<ColumnTitle>{intl_tokens.Prio}</ColumnTitle>}
          description={<ColumnTitle>{intl_tokens.Description}</ColumnTitle>}
          pulishedAt={<ColumnTitle>{intl_tokens.Published}</ColumnTitle>}
          archivedAt={<ColumnTitle>{intl_tokens.Archived}</ColumnTitle>}
        />
      </ListItem>
      <AHSeparator />
    </Box>
  );
}
